<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { messageTimestamp, dynamicTime } from 'shared/helpers/timeHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  createdAt: { type: [Number, String], default: null },
  lastActivityAt: { type: [Number, String], default: null },
  contactInboxes: { type: Array, default: () => [] },
  contact_inboxes: { type: Array, default: () => [] },
  referer: { type: String, default: '' },
  pageVisits: { type: Array, default: () => [] },
  page_visits: { type: Array, default: () => [] },
  source: { type: String, default: '' },
});

const emit = defineEmits(['toggle', 'updateContact', 'showContact']);

const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');

const contactsFormRef = ref(null);

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  phoneNumber: props.phoneNumber,
  additionalAttributes: props.additionalAttributes,
  referer: props.referer,
  pageVisits: props.pageVisits || props.page_visits,
  source: props.source,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    if (country.dial_code) acc[country.dial_code] = country;
    if (country.id) acc[country.id] = country;
    return acc;
  }, {});
});

const countryDetails = computed(() => {
  const attributes = props.additionalAttributes || {};
  const { country, countryCode, city } = attributes;

  if (!country && !countryCode) return null;

  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];

  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    city: city ? `${city},` : null,
    name: activeCountry.name,
  };
});

const formattedLocation = computed(() => {
  if (!countryDetails.value) return '';

  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(' ');
});

const formattedCreatedAt = computed(() => {
  if (!props.createdAt) return '';
  const timestamp = typeof props.createdAt === 'string' 
    ? new Date(props.createdAt).getTime() / 1000 
    : props.createdAt;
  return messageTimestamp(timestamp, 'MMM d, yyyy h:mm a');
});

const formattedLastActivityAt = computed(() => {
  if (!props.lastActivityAt) return '';
  const timestamp = typeof props.lastActivityAt === 'string' 
    ? new Date(props.lastActivityAt).getTime() / 1000 
    : props.lastActivityAt;
  return messageTimestamp(timestamp, 'MMM d, yyyy h:mm a');
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const onClickViewDetails = () => emit('showContact', props.id);

const copyContactDetails = async () => {
  const attr = props.additionalAttributes || {};
  const contactInboxes = props.contactInboxes || props.contact_inboxes || [];
  const contactInbox = contactInboxes[0];
  const inboxId =
    attr.inboxId ||
    attr.inbox_id ||
    contactInbox?.inboxId ||
    contactInbox?.inbox_id ||
    contactInbox?.inbox?.id;

  const inbox = inboxes.value.find(i => Number(i.id) === Number(inboxId));
  const channelName = (contactInbox?.inbox?.name) || (inbox ? inbox.name : 'WebWidget');

  const createdAt = props.createdAt;
  const formattedDate = createdAt
    ? new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      })
        .format(
          new Date(typeof createdAt === 'string' ? createdAt : createdAt * 1000)
        )
        .toLowerCase()
    : '';

  const branch = [attr.city, attr.country].filter(Boolean).join(' - ');

  const pageVisits = props.pageVisits || props.page_visits || attr.page_visits || attr.pageVisits || [];
  const firstVisit = pageVisits.length > 0 ? pageVisits[0] : null;

  const landingPage =
    (firstVisit ? firstVisit.title || firstVisit.url : null) ||
    props.referer ||
    attr.referer ||
    attr.referer_link ||
    attr.refererLink ||
    attr.referer_url ||
    attr.landing_page ||
    attr.landed_url ||
    'N/A';
  const source =
    props.source ||
    attr.source_display ||
    attr.sourceDisplay ||
    attr.utm_source ||
    attr.utmSource ||
    attr.source ||
    'Direct URL';
  const interestedCourse = attr.interested_course || attr.interestedCourse || '';
  const educationalQualification = attr.educational_qualification || attr.educationalQualification || '';

  const excelFormat = [
    formattedDate,
    props.name || '',
    props.email || '',
    props.phoneNumber || '',
    branch || '',
    attr.description || '',
    landingPage || '',
    source || 'Direct URL',
    interestedCourse || '',
    educationalQualification || '',
    channelName,
  ].join('\t');

  try {
    await copyTextToClipboard(excelFormat);
    useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
  } catch {
    useAlert('Unable to copy details');
  }
};
</script>

<template>
  <CardLayout :key="id" layout="row">
    <div class="flex items-center justify-start flex-1 gap-4">
      <Avatar
        :name="name"
        :src="thumbnail"
        :size="48"
        :status="availabilityStatus"
        hide-offline-status
        rounded-full
      />
      <div class="flex flex-col gap-0.5 flex-1">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span class="text-base font-medium truncate text-n-slate-12">
            {{ name }}
          </span>
          <span class="inline-flex items-center gap-1">
            <span
              v-if="
                additionalAttributes?.interestedCourse ||
                additionalAttributes?.interested_course
              "
              class="i-ph-book-light size-4 text-n-slate-10 mb-0.5"
            />
            <span
              v-if="
                additionalAttributes?.interestedCourse ||
                additionalAttributes?.interested_course
              "
              class="text-sm truncate text-n-slate-11"
            >
              {{
                additionalAttributes?.interestedCourse ||
                additionalAttributes?.interested_course
              }}
            </span>
          </span>
          <span class="inline-flex items-center gap-1">
            <span
              v-if="
                additionalAttributes?.educationalQualification ||
                additionalAttributes?.educational_qualification
              "
              class="i-ph-graduation-cap-light size-4 text-n-slate-10 mb-0.5"
            />
            <span
              v-if="
                additionalAttributes?.educationalQualification ||
                additionalAttributes?.educational_qualification
              "
              class="text-sm truncate text-n-slate-11"
            >
              {{
                additionalAttributes?.educationalQualification ||
                additionalAttributes?.educational_qualification
              }}
            </span>
          </span>
        </div>
        <div class="flex flex-wrap items-center justify-start gap-x-3 gap-y-1">
          <div v-if="email" class="truncate max-w-72" :title="email">
            <span class="text-sm text-n-slate-11">
              {{ email }}
            </span>
          </div>
          <div v-if="email" class="w-px h-3 truncate bg-n-slate-6" />
          <span v-if="phoneNumber" class="text-sm truncate text-n-slate-11">
            {{ phoneNumber }}
          </span>
          <div v-if="phoneNumber" class="w-px h-3 truncate bg-n-slate-6" />
          <span
            v-if="countryDetails"
            class="inline-flex items-center gap-2 text-sm truncate text-n-slate-11"
          >
            <Flag :country="countryDetails.countryCode" class="size-3.5" />
            {{ formattedLocation }}
          </span>
          <div v-if="countryDetails" class="w-px h-3 truncate bg-n-slate-6" />
          <span
            v-if="formattedCreatedAt"
            class="text-sm truncate text-n-slate-11"
            :title="formattedCreatedAt"
          >
            {{ formattedCreatedAt }}
          </span>
          <div v-if="formattedCreatedAt" class="w-px h-3 truncate bg-n-slate-6" />
          <span
            v-if="formattedLastActivityAt"
            class="text-sm truncate text-n-slate-11"
            :title="formattedLastActivityAt"
          >
            {{ formattedLastActivityAt }}
          </span>
          <div v-if="formattedLastActivityAt" class="w-px h-3 truncate bg-n-slate-6" />
          <Button
            :label="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
            variant="link"
            size="xs"
            @click="onClickViewDetails"
          />
          <div class="w-px h-3 truncate bg-n-slate-6" />
          <Button
            v-tooltip.top="t('CONTACT_PANEL.COPY_DETAILS')"
            icon="i-ph-copy"
            variant="link"
            size="xs"
            color="slate"
            @click="copyContactDetails"
          />
        </div>
      </div>
    </div>

    <Button
      icon="i-lucide-chevron-down"
      variant="ghost"
      color="slate"
      size="xs"
      :class="{ 'rotate-180': isExpanded }"
      @click="onClickExpand"
    />

    <template #after>
      <div
        class="transition-all duration-500 ease-in-out grid overflow-hidden"
        :class="
          isExpanded
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0'
        "
      >
        <div class="overflow-hidden">
          <div class="flex flex-col gap-6 p-6 border-t border-n-strong">
            <ContactsForm
              ref="contactsFormRef"
              :contact-data="contactData"
              @update="handleFormUpdate"
            />
            <div>
              <Button
                :label="
                  t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')
                "
                size="sm"
                :is-loading="isUpdating"
                :disabled="isUpdating || isFormInvalid"
                @click="handleUpdateContact"
              />
            </div>
          </div>
          <ContactDeleteSection
            :selected-contact="{
              id: props.id,
              name: props.name,
            }"
          />
        </div>
      </div>
    </template>
  </CardLayout>
</template>
